import { TextInput, Button, FileInput } from "flowbite-react";
import { FaSmile, FaFile, FaFileAlt, FaFileImage, FaFilePdf, FaFileWord, FaFileExcel, FaFileVideo, FaFileAudio, FaExclamationTriangle } from "react-icons/fa";
import { HiPaperClip, HiPaperAirplane } from "react-icons/hi";
import React, {
  FunctionComponent,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { generateSHA1 } from "../utils/hashUtils";
import { IoCloseSharp } from "react-icons/io5";
import {
  createRequestControlMessageReadyParam,
} from "../utils/cmsgBuilder";
import {
  createEmptyMultiPart,
  MessagePart,
} from "../utils/multiPartBuilder";
import generateRandomString from "../MSRP/utilities/generators";
import Wrapper from "../utils/msrpWrapper";
import Global from "../globals/globals";
import { ChatRoom } from "../models/ChatRoom";
import { MyRequest } from "../models/MyRequest";
import { ChatContext, ChatContextInterface } from "./MSRContext";
import { Message } from "../models/Message";
import { FileMessage } from "../models/FileMessage";
import { IoMdClose } from "react-icons/io";
import { FiCornerDownLeft } from "react-icons/fi";
import { toast } from "react-toastify";
import { useSidebarContext } from "../context/SidebarContext";
import { SeenEntry } from "@/models/ChatMessage";

interface MessageInputProps {
  onSendMessage: (
    text: string,
    sender: string,
    isOwner: boolean,
    id: string
  ) => void;
  onSendFileMessage?: (fileMessage: any) => void;
  wrapper: Wrapper | null;
  chatroomusername: string;
  roomType: string;
  currentRoom: any | null;
  repliedMessage: any | null;
  setRepliedMessage: (message: any | null) => void;
  isFixed?: boolean; // Whether to use fixed positioning (default: true for backward compatibility)
}

async function uploadFileToServer(file: File): Promise<string> {

  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  if (file.size > MAX_FILE_SIZE) { toast.error("The file you have selected is too large. The maximum size is 20MB."); }

  const formData = new FormData();
  formData.append("files", file);

  // Use your actual upload endpoint
  const response = await fetch("https://console.icont.ai/api/upload", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("File upload failed");
  }
  const result = await response.json();
  return result.data[0].file_url;
}

// Helper function to get appropriate icon for file type
const getFileIcon = (fileType: string) => {
  if (fileType.startsWith('image/')) return <FaFileImage className="text-blue-500 text-2xl" />;
  if (fileType === 'application/pdf') return <FaFilePdf className="text-red-500 text-2xl" />;
  if (fileType.includes('word') || fileType.includes('document')) return <FaFileWord className="text-blue-600 text-2xl" />;
  if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <FaFileExcel className="text-green-600 text-2xl" />;
  if (fileType.startsWith('video/')) return <FaFileVideo className="text-purple-500 text-2xl" />;
  if (fileType.startsWith('audio/')) return <FaFileAudio className="text-yellow-500 text-2xl" />;
  if (fileType.startsWith('text/')) return <FaFileAlt className="text-gray-600 text-2xl" />;
  return <FaFile className="text-gray-500 text-2xl" />;
};

// Helper function to get a human-readable file size
const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes < 1024) return `${sizeInBytes} B`;
  else if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  else if (sizeInBytes < 1024 * 1024 * 1024) return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
  else return `${(sizeInBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const MessageInput: FunctionComponent<MessageInputProps> = ({
  onSendMessage,
  onSendFileMessage,
  wrapper,
  chatroomusername,
  roomType,
  currentRoom,
  repliedMessage,
  setRepliedMessage,
  isFixed = true, // Default to true for backward compatibility
}) => {

  const [message, setMessage] = useState("");
  const [fileTitle, setFileTitle] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const { handleSendFileMessage, handleaddServerRequest } =
    useContext(ChatContext) as ChatContextInterface;

  const isAgentToAgent = currentRoom?.session_type === "agent-to-agent" ? true : null;
  const isClientToAgent = currentRoom?.session_type === "c2a";
  const isGroupChat = currentRoom?.session_type === "group-chat";
  const isConference = currentRoom?.session_type === "conference";
  const shouldAddDomain = isAgentToAgent || isClientToAgent || isGroupChat;
  const toName = isGroupChat || isConference ? currentRoom.session_key : chatroomusername  + (shouldAddDomain ? Global.companyDomain : "");

  useEffect(() => {
    if (currentRoom?.isSessionActive === false) {
      // toast.error("Cannot send message. The session is closed.");
    }
  }, [currentRoom?.isSessionActive]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        const hasText = message.trim().length > 0;
        const hasFiles = selectedFiles.length > 0;

        if (hasText || hasFiles) {
          event.preventDefault();
          handleSend();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFiles, message]);

  // Generate previews when files are selected
  useEffect(() => {
    // Clean up previous preview URLs
    filePreviews.forEach(url => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    });

    // Generate new previews
    const newPreviews = selectedFiles.map(file => {
      if (file.type.startsWith('image/')) {
        return URL.createObjectURL(file);
      }
      return '';
    });

    setFilePreviews(newPreviews);

    // Cleanup on unmount
    return () => {
      newPreviews.forEach(url => {
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
    };
  }, [selectedFiles]);

  // Handler for file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    // Convert FileList to an array
    const newFiles = Array.from(event.target.files);
    // Append new files to existing
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    // Clear the file input so the same file can be selected again if needed
    event.target.value = "";
  };

  // Remove a file from the preview list
  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    // Preview URL will be cleaned up in the useEffect
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.emoji;
    const textarea = textareaRef.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentMessage = message;

      const newMessage = currentMessage.slice(0, start) + emoji + currentMessage.slice(end);
      setMessage(newMessage);

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    }

  };

  const handleEmojiButtonClick = () => {

    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleSendTextMessage = (
    multipartMessage: string,
    boundary: string,
    headerMsgId: string,
    requestID: string
  ) => {
    console.log("Sending text message with ID:", headerMsgId, "and request ID:", requestID);
    const contentType = `multipart/related;boundary="${boundary}"`;
     wrapper?.sendMsg(multipartMessage, contentType, headerMsgId);

    // Always show the optimistic message immediately
    onSendMessage(message, Global.username + Global.companyDomain, true, headerMsgId);

    // If replying to a specific message, add that request
    if (repliedMessage) {
      console.log("replied message exists:", repliedMessage);
      const newRequest: MyRequest = {
        id: requestID,
        sender: Global.username + Global.companyDomain,
        receiver: chatroomusername,
        type: "reply-to",
        status: currentRoom?.session_type ?? "pending",
        detail: message.trim(),
        incoming: false,
        createdTime: new Date().toISOString(),
        messageID: repliedMessage.messageId,
        headerMsgId: headerMsgId,
      };

      console.log("Adding reply-to request:", newRequest);
      handleaddServerRequest(newRequest);
    }
    // Reset
    setMessage("");
    setSelectedFiles([]);
    setIsUrgent(false);
  };

  const handleSend = async () => {
    console.log("Handle send triggered");
    if (!currentRoom?.isSessionActive) {
      toast.error("Cannot send message. The session is closed.");
      return;
    }

    const hasText = message.trim().length > 0;
    const hasFiles = selectedFiles.length > 0;

    if (!hasText && !hasFiles) {
      return;
    }

    setShowEmojiPicker(false);

    // Prepare the basic multipart for the text portion only (no file bytes)
    const multipartMessage = createEmptyMultiPart();
    const messageId = generateRandomString(16);
    const headerMsgId = generateRandomString(16);
    const messageToType = isAgentToAgent && isUrgent ? "urgent:a2a" : roomType;

    // If we're replying to a message, send a control part referencing the old message
    if (repliedMessage) {
      const part1 = new MessagePart({
        name: "control",
        contentType: "application/rechat.cntl+json\r\n",
        filename: "control.json",
        value: JSON.stringify({
          id: messageId,
          type: "request",
          job: "reply-to",
          parameters: {
            "message-id": repliedMessage.messageId,
            "from-name": Global.username + Global.companyDomain,
            "to-name": toName,
          },
        }),
        contentDisposition: "attachment",
      });
      multipartMessage.addPart(part1);

      // Part 2: Text message (if any)
      if (message.trim()) {
        const part2 = new MessagePart({
          name: "text",
          contentType: "text/plain\r\n",
          filename: "message.txt",
          value: message,
          contentDisposition: "attachment",
        });
        multipartMessage.addPart(part2);
      }
    } else {
      // Normal text message (not a reply)
      const part1 = new MessagePart({
        name: "control",
        contentType: "application/rechat.cntl+json\r\n",
        filename: "control.json",
        value: JSON.stringify({
          id: messageId,
          type: "request",
          job: "message-to",
          parameters: {
            "from-name": Global.username + Global.companyDomain,
            "to-name": toName,
            "message-to-type": messageToType,
          },
        }),
        contentDisposition: "attachment",
      });
      multipartMessage.addPart(part1);

      if (message.trim()) {
        const part2 = new MessagePart({
          name: "text",
          contentType: "text/plain\r\n",
          filename: "message.txt",
          value: message,
          contentDisposition: "attachment",
        });
        multipartMessage.addPart(part2);
      }
    }
    for (const file of selectedFiles) {
      try {
        const fileUrl = await uploadFileToServer(file);
        const arrayBuffer = await file.arrayBuffer();
        const fileHash = generateSHA1(new Uint8Array(arrayBuffer));
        const singleFileMessageId = generateRandomString(16);
        const fileMessageId = generateRandomString(16); // Unique ID for THIS file message
        const fileTransferParams = {
          direction: "send",
          "from-name": Global.username + Global.companyDomain,
          "target-name": toName,
          "file-name": file.name,
          "file-type": file.type,
          "file-size": file.size,
          "file-hash": fileHash,
          "file-disposition": "located",
          "file-location": fileUrl,
          "file-title": fileTitle || file.name, // Use file name as default if no title provided
          "file-transfer-id": generateRandomString(32),
          "file-creation-date": new Date().toISOString(),
          "file-modification-date": "Mon, 15 May 2006 15:01:31 +0300",
          "file-read-date": "Mon, 15 May 2006 15:01:31 +0300"
        };

        console.log("singleFileMessageId:", singleFileMessageId);
        console.log("fileMessageId (unique for this file):", fileMessageId);
        wrapper?.sendMsg(
          createRequestControlMessageReadyParam(
            singleFileMessageId,
            "file-transfer",
            fileTransferParams
          ),
          "application/rechat.cntl+json",
          fileMessageId, // Use unique file message ID

        );

        let sentTime = new Date();
        let deliveredTime = null;
        let seenTimes: SeenEntry[] = [];
        let editedTime = null;
        let deletedTime = null;


        const fileMsg = new FileMessage(
          fileTitle || file.name,
          file.name,
          fileUrl,
          file.type,
          fileMessageId, // Use unique file message ID
          true,
          currentRoom.chatRoomId,
          Global.username + Global.companyDomain,
          repliedMessage,
          sentTime,
          deliveredTime,
          seenTimes,
          editedTime,
          deletedTime,
          fileMessageId // Use unique file message ID
        );

        // Add to old ChatContext state for backward compatibility
        handleSendFileMessage(fileMsg);

        // Add to new messages state for immediate display
        if (onSendFileMessage) {
          // Convert FileMessage to format compatible with ChatArea messages
          const optimisticFileMsg = {
            messageId: fileMessageId, // Use unique file message ID
            text: fileTitle || file.name, // Show file title as text
            isOwner: true,
            senderName: Global.username + Global.companyDomain,
            sentTime: sentTime.toISOString(),
            deliveredTime: null,
            arrivalTime: sentTime.toISOString(),
            seenTimes: [],
            editedTime: null,
            deletedTime: null,
            contentType: file.type,
            messageType: "file-transfer",
            fileName: file.name,
            filePath: fileUrl,
            fileType: file.type,
            message_json: {
              job: "file-transfer",
              parameters: {
                "file-name": file.name,
                "file-location": fileUrl,
                "file-type": file.type,
              }
            },
            repliedTo: repliedMessage ?? null
          };
          onSendFileMessage(optimisticFileMsg);
        }

      } catch (error) {
        console.error("File upload error", error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    console.log("Multipart message to send:", multipartMessage.toString());
    // Finally, send the text portion if there's any text or if it's a reply
    if (hasText || repliedMessage) {
      handleSendTextMessage(
        multipartMessage.toString(),
        multipartMessage.boundary,
        headerMsgId,
        messageId
      );
    } else {
      // If there's no text and not a reply, we still might want to reset state
      setMessage("");
      setSelectedFiles([]);
    }

    setFileTitle("");
    setRepliedMessage(null);

    // Clean up any blob URLs
    filePreviews.forEach(url => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    });
    setFilePreviews([]);
  };

  useEffect(() => {
    setRepliedMessage(null);
    setMessage("");
  }, [currentRoom])


  const containerClasses = isFixed
    ? `fixed bottom-0 right-0 left-0  bg-white dark:border-gray-700 dark:bg-gray-800 transition-all duration-300 ease-in-out ml-0 lg:ml-[400px]`
    : `relative bg-white dark:border-gray-700 dark:bg-gray-800 transition-all duration-300 ease-in-out`;
  return (
    <div className={containerClasses}>
      {/* File preview area */}
      {selectedFiles.length > 0 && (
        <div className="flex overflow-x-auto p-2 sm:p-3 gap-2 sm:gap-3 border-b border-gray-200 dark:border-gray-700 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
          {selectedFiles.map((file, index) => (
            <div
              key={index}
              className="relative flex flex-col items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-2 sm:p-3 min-w-[100px] sm:min-w-[120px] max-w-[120px] sm:max-w-[150px] flex-shrink-0"
            >
              {/* Preview for images */}
              {file.type.startsWith('image/') ? (
                <div className="w-full h-16 sm:h-20 md:h-24 mb-2 overflow-hidden rounded">
                  <img
                    src={filePreviews[index]}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center w-full h-16 sm:h-20 md:h-24 mb-2">
                  {getFileIcon(file.type)}
                </div>
              )}

              {/* File info */}
              <div className="flex flex-col items-center w-full">
                <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate max-w-full text-center">
                  {file.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.size)}
                </span>
              </div>

              {/* Remove button */}
              <button
                onClick={() => handleRemoveFile(index)}
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors touch-manipulation"
                aria-label="Remove file"
              >
                <IoCloseSharp size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Reply preview */}
      {repliedMessage && (
        <div className="p-2 sm:p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <div className="flex-shrink-0 mt-1">
              <FiCornerDownLeft size={14} className="sm:w-4 sm:h-4 text-purple-500" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="p-2 sm:p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-l-4 border-purple-400 shadow-sm">
                <div className="mb-1">
                  <span className="text-xs font-medium text-purple-900 dark:text-purple-300">
                    Replying to: {repliedMessage.senderName?.split("@")[0] || repliedMessage.sender?.agent_username?.split("@")[0] || repliedMessage.sender?.client_username?.split("@")[0] || "Unknown"}
                  </span>
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2 sm:line-clamp-3 break-words">
                  {(() => {
                    const msgAny = repliedMessage as any;
                    // Check if it's a file message
                    const isFileMsg = msgAny.messageType === "file-transfer" || msgAny.fileName || msgAny.filePath || msgAny.media_url;

                    if (isFileMsg) {
                      const fileName = msgAny.fileName || "File";
                      return `📎 ${fileName}`;
                    }

                    // Regular text message - avoid showing JSON
                    const text = repliedMessage.text || "";
                    if (!text.trim() || text.trim().startsWith('{') || text.trim().startsWith('[')) {
                      return "Message";
                    }

                    return text;
                  })()}
                </p>
              </div>
            </div>

            <button
              className="flex-shrink-0 p-1.5 sm:p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 touch-manipulation"
              onClick={() => setRepliedMessage(null)}
              aria-label="Cancel reply"
            >
              <IoMdClose size={16} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            </button>
          </div>
        </div>
      )}

      {showEmojiPicker && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center " onClick={() => setShowEmojiPicker(false)}>
          <div
            className="relative bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-600"
            ref={emojiPickerRef}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowEmojiPicker(false)}
              className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              aria-label="Close emoji picker"
            >
              <IoMdClose size={16} className="text-gray-600 dark:text-gray-300" />
            </button>

            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
              theme={document.documentElement.classList.contains('dark') ? 'dark' as any : 'light' as any}
              width={350}
              height={450}
              previewConfig={{
                showPreview: false
              }}

            />
          </div>
        </div>
      )}

      {/* Message input area */}
      <div className="p-2 sm:p-3 md:p-4 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center space-x-2 sm:space-x-3">
          {/* Action buttons - Left side */}
          <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
            {/* Emoji button */}
            <button
              onClick={handleEmojiButtonClick}
              className={`p-2 sm:p-2.5 rounded-full transition-colors duration-200 touch-manipulation ${showEmojiPicker
                ? 'bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-300'
                : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                }`}
              aria-label="Add emoji"
            >
              <FaSmile className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            {/* File attachment button */}
            <label
              htmlFor="file-input"
              className="flex items-center p-2 sm:p-2.5 text-gray-500 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors duration-200 touch-manipulation"
              aria-label="Attach file"
            >
              <HiPaperClip className="w-5 h-5 sm:w-6 sm:h-6" />
            </label>
            <input
              id="file-input"
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv" />

            {isAgentToAgent && !repliedMessage && (
              <div className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  id="urgent-checkbox"
                  checked={isUrgent}
                  onChange={(e) => setIsUrgent(e.target.checked)}
                  className="w-3.5 h-3.5 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 dark:focus:ring-orange-600 dark:ring-offset-gray-800 focus:ring-1 dark:bg-gray-700 dark:border-gray-600"
                />
                <label
                  htmlFor="urgent-checkbox"
                  className="flex items-center space-x-1 cursor-pointer"
                >
                  <FaExclamationTriangle className={`w-3 h-3 ${isUrgent ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`} />
                  <span
                    className={`text-xs font-medium ${isUrgent ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
                    {isUrgent ? 'Urgent' : 'Urgent'}
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Message input container */}
          <div className="flex-1 flex items-end">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={currentRoom?.isSessionActive ? (selectedFiles.length > 0 ? "Add a message (optional)" :  "Type your message here..."): "This session is closed"}
              disabled={!currentRoom?.isSessionActive}
              rows={1}
              id="message-to"
              name="message-to"
              className="flex w-full min-h-[44px] sm:min-h-[48px] px-3 sm:px-4 py-2 sm:py-3 bg-gray-100 dark:bg-gray-700 rounded-2xl sm:rounded-3xl border border-gray-200 dark:border-gray-600 focus:border-purple-400 dark:focus:border-purple-500 focus:outline-none focus:ring-0 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none overflow-y-auto transition-colors duration-200 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
              style={{
                minHeight: '20px',
                maxHeight: '120px',
                overflowY: 'auto',
              }}
            />
          </div>

          {/* Send button */}
          <div className="flex-shrink-0">
            <button
              onClick={handleSend}
              disabled={!currentRoom?.isSessionActive || (!message.trim() && selectedFiles.length === 0)}
              className={`
              p-2.5 sm:p-3 rounded-full transition-all duration-200 touch-manipulation
              ${(!currentRoom?.isSessionActive || (!message.trim() && selectedFiles.length === 0))
                  ? 'bg-purple-100 dark:bg-gray-600 cursor-not-allowed'
                  : isUrgent && isAgentToAgent
                    ? 'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 hover:scale-105 active:scale-95'
                    : 'bg-purple-500 hover:bg-purple-600 active:bg-purple-700 hover:scale-105 active:scale-95'
                }
            `}
              aria-label="Send message"
            >
              <HiPaperAirplane className="w-4 h-4 rounded-full sm:w-6 sm:h-6 text-white transform rotate-0 hover:rotate-12 transition-transform duration-200" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default MessageInput;